import {
  Box,
  Button,
  chakra,
  Heading,
  PinInput,
  PinInputField,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import { DateTime } from 'luxon'
import pMap from 'p-map'
import { useCallback, useRef, useState } from 'react'

import { joinWithConjunction, noOp } from '@/utils/helpers'
import logger from '@/utils/logger'
import normalizeBirthday from '@/utils/normalizeBirthday'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC, { RPCRefresh } from '@/client/rpc'
import { emailMatchesCollege } from '@/client/services/college'
import uploadFile from '@/client/services/userUpload/uploadFile'

import FileUploadDropzone from '@/components/FileUploadDropzone/FileUploadDropzone'
import { useToaster } from '@/components/Toast/useToaster'
import Alert from '@/components/UI/Alert'
import Form, { type FormRef } from '@/components/UI/Form/Form'
import FormFooterEmpty from '@/components/UI/Form/FormFooterEmpty'
import { InputText } from '@/components/UI/Form/styled'
import type { FormValues } from '@/components/UI/Form/types'
import { resetFormStates } from '@/components/UI/FormBuilder/helpers'
import ModalV3 from '@/components/UI/Modal/ModalV3'

import useCollegeInfoFormInputs from './useCollegeInfoFormInputs'

type VerificationType = 'email' | 'document'
export default function CreateCollegeInfoModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}): JSX.Element {
  const currentUser = useUser()
  const toast = useToaster()

  const { inputs, college, collegeOtherDetails } = useCollegeInfoFormInputs()

  const [submitting, setSubmitting] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const [values, setValues] = useState<FormValues>({})
  const [selectedVerificationType, setSelectedVerificationType] = useState<VerificationType | null>(
    null
  )
  const [email, setEmail] = useState<string | null>(null)
  const [emailWithVerificationSent, setEmailWithVerificationSent] = useState<string | null>(null)
  const [verificationCode, setVerificationCode] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [fileUploadStatus, setFileUploadStatus] = useState<{
    status: 'uploading'
    uploaded: number
    total: number
  } | null>(null)

  const formRef = useRef<FormRef>(null)

  const onModalClose = useCallback(() => {
    onClose()
    setFileUploadStatus(null)
    setAttachments([])
    setValues({})
  }, [onClose])

  const handleSubmit = useCallback(async () => {
    setErrorMessage(null)
    setSubmitting(true)

    try {
      const validationResult = await formRef.current?.validate()

      if (validationResult != null && validationResult.success === false) {
        return
      }

      const userEmailMatchesCollege = emailMatchesCollege({ email: currentUser.email, college })

      const verificationType =
        collegeOtherDetails != null
          ? 'document'
          : userEmailMatchesCollege
            ? 'email'
            : selectedVerificationType

      if (verificationType == null) {
        setErrorMessage('Please verify your college information.')
        return
      }

      const graduationMonth = Number.parseInt(`${values.graduationMonth}`, 10)
      const graduationYear = Number.parseInt(`${values.graduationYear}`, 10)
      const major = `${values.major}`
      const name = collegeOtherDetails != null ? collegeOtherDetails.name : `${values.name}`
      const visible = (values.visible as string[]).includes('yes')

      const graduationDate = normalizeBirthday(
        DateTime.fromObject({
          year: graduationYear,
          month: graduationMonth,
        })
      ).toISO()

      let attachmentUrls: string[] = []
      if (verificationType === 'document') {
        if (attachments.length === 0) {
          setErrorMessage('Please upload at least one attachment.')
          return
        }
        setFileUploadStatus({ uploaded: 0, total: attachments.length, status: 'uploading' })

        let filesUploaded = 0
        attachmentUrls = await pMap(
          attachments,
          async file => {
            try {
              const { downloadUrl } = await uploadFile({
                file,
                fileType: file.type,
                target: { type: 'attachment' },
              })
              filesUploaded += 1
              setFileUploadStatus({
                uploaded: filesUploaded,
                total: attachments.length,
                status: 'uploading',
              })
              return downloadUrl
            } catch (err) {
              logger.error('Error uploading file', { err })

              setFileUploadStatus(null)
              throw new Error('There was an error uploading one or more files. Please try again.')
            }
          },
          { concurrency: 3 }
        )
      }

      if (verificationType === 'email' && !userEmailMatchesCollege && verificationCode == null) {
        setErrorMessage('Please verify your email address before submitting.')
        return
      }

      const countryCode =
        college?.alpha_two_code ?? (values.countryOther == null ? null : `${values.countryOther}`)

      if (countryCode == null) {
        setErrorMessage('Please select a country.')
        return
      }

      setErrorMessage(null)

      const verified = await RPCRefresh.submitCollegeInfo({
        collegeInfo: {
          name,
          graduationDate,
          major,
          attachmentUrls: verificationType === 'document' ? attachmentUrls : [],
        },
        emailInfo:
          verificationType === 'email' &&
          verificationCode != null &&
          emailWithVerificationSent != null
            ? {
                email: emailWithVerificationSent,
                verificationCode,
              }
            : null,
        visible,
        countryCode,
      })

      if (verified) {
        toast({
          title: 'Your college information was verified!',
          status: 'success',
        })
      } else {
        toast({
          title: 'Your college information was submitted. Please wait 1-5 days for review.',
          status: 'success',
        })
      }
      setErrorMessage(null)
      onClose()
    } catch (e) {
      setErrorMessage(e.message)
    } finally {
      setSubmitting(false)
      resetFormStates([formRef.current])
      onModalClose()
    }
  }, [
    collegeOtherDetails,
    selectedVerificationType,
    values.graduationMonth,
    values.graduationYear,
    values.major,
    values.name,
    values.visible,
    values.countryOther,
    verificationCode,
    emailWithVerificationSent,
    onClose,
    attachments,
    toast,
    onModalClose,
    college,
    currentUser.email,
  ])

  const [handleSendVerificationEmail, isSendingVerificationEmail] = useAPIWrapper(
    async () => {
      if (email == null || college == null) {
        return
      }
      if (!emailMatchesCollege({ email, college })) {
        setErrorMessage('Email does not match your college')
        return
      }
      setEmailWithVerificationSent(email)
      await RPC.sendSecondaryEmailVerification({ email })
    },
    [email, college],
    'Send Verification Email'
  )

  return (
    <ModalV3
      open={open}
      title="Submit College Info"
      size="3xl"
      body={() => (
        <Stack direction="column" spacing="24px">
          {errorMessage == null ? null : <Alert variant="error">{errorMessage}</Alert>}
          {fileUploadStatus == null ? null : (
            <Alert variant="info-blue" excludeIcon>
              <Stack direction="row" align="center" spacing="8px">
                <Spinner size="sm" thickness="2px" />
                <Text>
                  Uploading files... ({fileUploadStatus.uploaded}/{fileUploadStatus.total})
                </Text>
              </Stack>
            </Alert>
          )}
          <Form
            name="CollegeInfoForm"
            blocks={inputs}
            onSubmit={noOp}
            FooterComponent={FormFooterEmpty}
            ref={formRef}
            onChange={(_k, _v, formValues) => setValues(formValues)}
          />

          {college != null || collegeOtherDetails != null ? (
            <Heading size="lg">Verify Your College Affiliation</Heading>
          ) : null}
          {emailMatchesCollege({ email: currentUser.email, college }) && college != null ? (
            <Alert variant="success">
              Your email address ({currentUser.email}) is affiliated with {college.name}!
            </Alert>
          ) : college != null || collegeOtherDetails != null ? (
            <Box>
              {college != null ? (
                <>
                  <Text fontWeight="bold" mb="8px">
                    How would you like to verify your affiliation with this college?
                  </Text>
                  <Stack direction="column" spacing="4px" mb="24px">
                    <RadioGroup onChange={v => setSelectedVerificationType(v as VerificationType)}>
                      <Radio value="email">
                        Verify an email address ending with{' '}
                        <chakra.span fontWeight="bold">
                          {joinWithConjunction(college.domains, 'or')}
                        </chakra.span>{' '}
                        (instant)
                      </Radio>
                      <Radio value="document">
                        Upload a document indicating your affiliation with this college (1-5 days)
                      </Radio>
                    </RadioGroup>
                  </Stack>
                </>
              ) : null}
              {selectedVerificationType === 'document' || collegeOtherDetails != null ? (
                <Stack direction="column" spacing="16px">
                  <Text fontWeight="bold">
                    Please upload a document indicating your{' '}
                    <b>acceptance to, enrollment in, or graduation from</b> this college. For
                    example, this can be an acceptance letter, class schedule, or unofficial
                    transcript that shows your name and enrollment status. These attachments will be
                    deleted after review.
                  </Text>
                  <FileUploadDropzone
                    maxFiles={5}
                    maxSizeMegabytes={10}
                    onChange={setAttachments}
                    acceptedFileTypes={['image/jpeg', 'image/png', 'application/pdf']}
                  />
                </Stack>
              ) : college != null && selectedVerificationType === 'email' ? (
                <Box>
                  <Text fontWeight="bold" mb="12px">
                    Please enter your college email address, ending with{' '}
                    {joinWithConjunction(college.domains, 'or')}.
                  </Text>
                  <Stack direction="row" spacing="8px">
                    <InputText onChange={e => setEmail(e.target.value)} />
                    <Button
                      onClick={handleSendVerificationEmail}
                      variant="outline"
                      width="220px"
                      isDisabled={
                        !emailMatchesCollege({ email: email ?? '', college }) ||
                        isSendingVerificationEmail
                      }
                      isLoading={isSendingVerificationEmail}
                    >
                      Send Verification Email
                    </Button>
                  </Stack>
                  {emailWithVerificationSent == null ? null : (
                    <Box>
                      <Text fontWeight="bold" mb="12px" mt="16px">
                        Please enter the code sent to <b>{emailWithVerificationSent}</b>.
                      </Text>
                      <PinInput
                        otp
                        value={verificationCode ?? undefined}
                        onChange={setVerificationCode}
                        size="md"
                      >
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                        <PinInputField />
                      </PinInput>
                    </Box>
                  )}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Stack>
      )}
      onClose={onModalClose}
      rightFooterContent={() => (
        <Stack direction="row" spacing="8px">
          <Button variant="outline" onClick={onModalClose}>
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={submitting}
            isDisabled={
              (college != null &&
                !emailMatchesCollege({ email: currentUser.email, college }) &&
                selectedVerificationType == null) ||
              (collegeOtherDetails != null && attachments.length === 0)
            }
          >
            Submit
          </Button>
        </Stack>
      )}
    />
  )
}
